import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useForm } from "react-hook-form";

import store from '@/store/store';
import { authActions } from '@/store/actions/auth.actions';

import api from '@/services/api';

import useFetchFlagData from '@/utils/hooks/useFetchFlagData';

import AuthPageWrapper from '@/views/elements/AuthPageWrapper/AuthPageWrapper';
import FormInput from '@/views/components/FormInput/FormInput';
import AppAlert from '@/views/components/Alert/AppAlert';
import { cookieService } from "@/services/cookie";
import { checkInitialParams } from "@/utils/functions/checkInitialParams";

{/* urlAccessMode - 0: reader/editor/admin, 1: editor/admin , 2: admin only*/}

const Login = () => {
	// const storageCsrf = sessionStorage.getItem('csrf');
	const storageCsrf = cookieService.getToken();

	const dict = useSelector(state => state.app.dict);
	const { isLoggedIn, csrf, returnUrl, urlAccessMode } = useSelector(state => state.auth);
	const { tLoginPageTitle, tLogin, tLoginPlaceholder, tPassword, tPasswordPlaceholder, tLog, tResetPassword, tNoUser, tOr, tMimLogin } = dict.login;

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { register, handleSubmit, reset, watch } = useForm();

	const isMimAuthAvailable = useFetchFlagData('/auth/mim');

	const [ unauthorized, setUnauthorized ] = useState(false);

	const loginTxt = watch('login');
	const passwordTxt = watch('password');

	const getHost = () => {
		return new URL(window.location.href).origin;
	};

	const passAuthorized = useCallback((user, accessMode, retUrl) => {
		if (retUrl && (
			(user.role === 'admin') ||
				(user.role === 'expert') ||
                (user.role === 'editor' && accessMode < 2) ||
                (user.role === 'reader' && accessMode === 0)
		)) {
			const subPage = retUrl.split('?')[0];
			const locationSearch = retUrl.split('?')[1];
			const initialParams = checkInitialParams(null, locationSearch, user);

			if(subPage === '/letters') {
				return navigate(`/letters?${initialParams}`, { replace: true });
			}

			if(subPage === '/templates') {
				return navigate(`/templates?${initialParams}`, { replace: true });
			}

			return navigate(retUrl, { replace: true });
		}
		navigate('/letters', { replace: true });
	}, [ navigate ]);

	useEffect(() => {
		if (isLoggedIn && csrf && returnUrl) {
			api.get('/auth/me').then(({data}) => {
				dispatch(authActions.setUser(data));
				passAuthorized(data, urlAccessMode, returnUrl);
			});
		}
	}, []);

	useEffect(() => {
		if (storageCsrf) {
			dispatch(authActions.signIn({
				token: storageCsrf,
			}));
		}
	}, [ storageCsrf, dispatch ]);

	const onSubmit = data => {
		api.post(`/auth/sign-in`, data)
			.then(response => {
				dispatch(authActions.signIn({
					token: response.headers.authorization,
				}));
				api.get('/auth/me').then(({data}) => {
					passAuthorized(data, urlAccessMode, returnUrl);
				});
			})
			.catch((err) => {
				if (err.response.status === 401) {
					setUnauthorized(true);
					reset();
				}
			});
	};

	const getGeneratedAuthInput = (labelValue, inputName, inputType, placeholderValue) => (
		<FormInput
			labelValue={ labelValue }
			id={ inputName }
			{ ...register(inputName, {
				required: "required",
				minLength: {
					value: 2,
					message: "min length is 5",
				},
			}) }
			type={ inputType }
			placeholder={ placeholderValue }
		/>
	);

	return (
		<AuthPageWrapper pageTitle={ tLoginPageTitle }>
			<section className='login__form--wrapper'>
				<AppAlert />
				<form
					className='login__form--form'
					onSubmit={ handleSubmit(onSubmit) }
				>
					{ getGeneratedAuthInput(tLogin, 'login', 'text', tLoginPlaceholder) }
					{ getGeneratedAuthInput(tPassword, 'password', 'password', tPasswordPlaceholder) }

					{ unauthorized && <div className='login__form--error'>{ tNoUser }</div> }

					<Link
						className='login__form--reset-password'
						to='/reset-password'
					>
						{ tResetPassword }
					</Link>

					<button
						className={ `button__blue login__form--submit${(!loginTxt || loginTxt.length < 3 || !passwordTxt || passwordTxt.legth < 3) ? ' disabled' : ''}` }
						type='submit'
					>
						{ tLog }
					</button>
				</form>
			</section>

			{ !isMimAuthAvailable ?
				null
				:
				<section>
					<div className='login__mim--divider'>
						<span>{ tOr }</span>
					</div>
					<a
						className='login__mim--login'
						href={ `${store.getState().app.config.reactAppApi}/api/oauth2/authorize/mim?success-redirect-url=${getHost() + '/mim-success'}&failure-redirect-url=${getHost() + '/mim-failure'}` }
					>
						{ tMimLogin }
					</a>
				</section> }
		</AuthPageWrapper>
	);
};
export default Login;
